import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Loader2, ExternalLink, Lock, Download, Sparkles, ArrowUpRight,
  Users, Target, BarChart3, ShoppingBag, Building2, Compass,
  CheckCircle2, AlertCircle, TrendingUp, Heart, Activity,
} from 'lucide-react';

import {
  reportsApi,
  type ReportPayload,
  type Entitlement,
  type FreeReportSections,
  type PremiumReportSections,
} from '../lib/reportsApi';
import {
  DEMO_REPORT_PAYLOAD,
  DEMO_REPORT_ENTITLEMENT,
} from '../demo/reportFixture';
import { downloadReportPdfLocal, renderAndUploadReportPdf } from '../lib/reportPdf';
import {
  Donut, ChartLegend, HorizontalBar, StackedBar,
  TrendLine, RadarScore, PeerScatter, Heatmap, KpiValue,
  CHART_PALETTE,
} from '../components/charts/ReportChart';
import {
  SectionCard, Button, Badge, LockedTeaser, SectionNav, EmptyState,
} from '../components/ui/light';
import { proxyImageUrl } from '../api';

const SECTIONS = [
  { id: 'snapshot',     label: 'Creator Snapshot',    icon: Users,       locked: false },
  { id: 'audience',     label: 'Audience',            icon: Compass,     locked: false },
  { id: 'cohort',       label: 'Cohort Benchmark',    icon: BarChart3,   locked: false },
  { id: 'monetization', label: 'Monetization',        icon: TrendingUp,  locked: false },
  { id: 'content',      label: 'Content Signals',     icon: Activity,    locked: false },
  { id: 'products',     label: 'Product Matches',     icon: ShoppingBag, locked: true  },
  { id: 'vendors',      label: 'Vendor Matches',      icon: Building2,   locked: true  },
  { id: 'actions',      label: 'Recommended Actions', icon: Target,      locked: false },
] as const;

type SectionId = typeof SECTIONS[number]['id'];

function fmtNumber(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

function fmtUsd(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

export default function ReportView() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Demo mode = `/reports/demo` (or its `/demo-report` alias). We
  // detect it from the pathname rather than `useParams().id` because
  // the demo route is a *literal* React Router path (no `:id`
  // segment), so `id` is `undefined` there. Without this fallback the
  // page hangs on a spinner forever for guests.
  //
  // We hydrate state SYNCHRONOUSLY for the demo so the first render
  // already shows the report; otherwise visitors hit a brief spinner
  // even though no network call is needed. For real reports we start
  // loading=true and fetch in the effect below.
  const isDemo = id === 'demo' || location.pathname === '/reports/demo';

  const [data, setData] = useState<{ report: ReportPayload; entitlement: Entitlement } | null>(
    isDemo ? { report: DEMO_REPORT_PAYLOAD, entitlement: DEMO_REPORT_ENTITLEMENT } : null
  );
  const [loading, setLoading] = useState(!isDemo);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState<SectionId>('snapshot');

  useEffect(() => {
    if (isDemo) return;
    if (!id) return;
    setLoading(true);
    reportsApi
      .get(Number(id))
      .then((r) => setData(r.data))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isDemo]);

  const isPremium = data?.entitlement.premium_unlocked ?? false;
  const sections = data?.report.sections ?? null;
  const [downloading, setDownloading] = useState(false);

  async function handleDownload() {
    if (!data) return;
    setDownloading(true);
    try {
      // Generate locally for instant feedback. For real reports, also
      // upload the PDF back so the snapshot has a server-stored copy
      // accessible via signed URL. The demo report has no DB row so
      // we only render the local copy.
      downloadReportPdfLocal(data.report.sections);
      if (!isDemo) {
        try {
          await renderAndUploadReportPdf(data.report.id, data.report.sections);
        } catch {
          // Storage upload failure should not block the user's local download.
        }
      }
    } finally {
      setDownloading(false);
    }
  }

  const navItems = useMemo(() =>
    SECTIONS.map((s) => ({
      id: s.id,
      label: s.label,
      locked: s.locked && !isPremium,
    })),
    [isPremium]
  );

  if (loading) {
    return (
      <div className="theme-light l-page min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--ch2-accent)' }} />
      </div>
    );
  }

  if (error || !data || !sections) {
    return (
      <div className="theme-light l-page min-h-screen p-10">
        <div className="l-shell">
          <EmptyState
            icon={<AlertCircle className="w-6 h-6" />}
            title={error || 'Report not found'}
            description="This report may have been removed or you may not have access."
            action={<Button onClick={() => navigate('/dashboard')}>Back to dashboard</Button>}
          />
        </div>
      </div>
    );
  }

  const free = sections as FreeReportSections;
  const premium = sections.variant === 'premium' ? (sections as PremiumReportSections) : null;

  return (
    <div className="theme-light l-page min-h-screen">
      {/* ── Sticky header bar ───────────────────────────────────── */}
      <div
        className="sticky top-0 z-20 backdrop-blur-md"
        style={{
          background: 'rgba(250,250,247,0.85)',
          borderBottom: '1px solid var(--ch2-border)',
        }}
      >
        <div className="l-shell py-4">
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate('/dashboard')}
                className="l-btn l-btn-ghost l-btn-sm -ml-2"
              >
                ← Back
              </button>
              <div className="min-w-0">
                <div className="l-section-eyebrow">CreatorHub360 Report</div>
                <h1
                  className="font-display font-semibold tracking-tight truncate"
                  style={{ fontSize: 18, color: 'var(--ch2-ink)' }}
                >
                  @{free.snapshot.handle} · {free.snapshot.platform}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {free.snapshot.is_mock && (
                <Badge variant="warning">Mock data</Badge>
              )}
              <Badge variant={isPremium ? 'accent' : 'neutral'}>
                {isPremium ? 'Premium' : 'Free'}
              </Badge>
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<Download className="w-4 h-4" />}
                onClick={handleDownload}
                loading={downloading}
              >
                PDF
              </Button>
              {!isPremium && (
                <Button
                  size="sm"
                  variant="accent"
                  iconLeft={<Sparkles className="w-4 h-4" />}
                  onClick={() => navigate('/upgrade')}
                >
                  Unlock Premium
                </Button>
              )}
            </div>
          </div>
          <SectionNav
            sections={navItems}
            activeId={activeSection}
            onSelect={(id) => {
              setActiveSection(id as SectionId);
              const el = document.getElementById(`section-${id}`);
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
          />
        </div>
      </div>

      <main className="l-shell py-8 space-y-12">
        {/* ── 1. Creator Snapshot ─────────────────────────────── */}
        <section id="section-snapshot">
          <SnapshotPanel sections={free} />
        </section>

        {/* ── 2. Audience Intelligence ───────────────────────── */}
        <section id="section-audience">
          <AudiencePanel sections={free} />
        </section>

        {/* ── 3. Cohort Benchmark ────────────────────────────── */}
        <section id="section-cohort">
          <CohortPanel sections={free} />
        </section>

        {/* ── 4. Monetization Opportunities ──────────────────── */}
        <section id="section-monetization">
          <MonetizationPanel sections={free} premium={premium} />
        </section>

        {/* ── 5. Content & Performance ───────────────────────── */}
        <section id="section-content">
          <ContentPanel sections={free} isPremium={isPremium} />
        </section>

        {/* ── 6. Product Matches (premium-gated) ─────────────── */}
        <section id="section-products">
          <ProductsPanel premium={premium} preview={free.premium_preview} />
        </section>

        {/* ── 7. Vendor Matches (premium-gated) ──────────────── */}
        <section id="section-vendors">
          <VendorsPanel premium={premium} preview={free.premium_preview} />
        </section>

        {/* ── 8. Recommended Actions ─────────────────────────── */}
        <section id="section-actions">
          <ActionsPanel sections={free} premium={premium} />
        </section>

        {/* ── Premium Unlock Preview ─────────────────────────── */}
        {!isPremium && (
          <section>
            <PremiumUnlockBanner preview={free.premium_preview} />
          </section>
        )}

        <footer className="text-center pt-12 pb-6">
          <p className="l-subtle text-sm">
            Generated by CreatorHub360 · Report ID #{data.report.id} ·{' '}
            {new Date(data.report.created_at).toLocaleDateString()}
          </p>
        </footer>
      </main>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Section panels
// ────────────────────────────────────────────────────────────────────────

function SnapshotPanel({ sections }: { sections: FreeReportSections }) {
  const s = sections.snapshot;
  return (
    <SectionCard
      eyebrow="01 — Creator Snapshot"
      title="Who you are at a glance"
      subtitle={s.niche_summary}
      variant="elevated"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Profile card */}
        <div className="lg:col-span-4 flex items-start gap-4">
          {s.profile_image_url ? (
            <img
              src={proxyImageUrl(s.profile_image_url) ?? undefined}
              alt={s.handle}
              referrerPolicy="no-referrer"
              className="w-20 h-20 object-cover shrink-0"
              style={{
                borderRadius: 'var(--ch2-radius-md)',
                border: '1px solid var(--ch2-border)',
              }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <div
              className="w-20 h-20 flex items-center justify-center shrink-0"
              style={{
                borderRadius: 'var(--ch2-radius-md)',
                background: 'var(--ch2-surface-2)',
              }}
            >
              <Users className="w-8 h-8" style={{ color: 'var(--ch2-ink-3)' }} />
            </div>
          )}
          <div className="min-w-0">
            <div
              className="font-display font-bold truncate"
              style={{ fontSize: 22, color: 'var(--ch2-ink)' }}
            >
              @{s.handle}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge variant="neutral">{s.platform}</Badge>
              {s.primary_category && <Badge variant="accent">{s.primary_category}</Badge>}
            </div>
            {s.profile_url && (
              <a
                href={s.profile_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-sm"
                style={{ color: 'var(--ch2-accent-2)' }}
              >
                View profile <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <p className="l-muted text-sm mt-3">{s.positioning}</p>
          </div>
        </div>

        {/* Stat grid */}
        <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiValue label="Followers"  value={fmtNumber(s.followers)} />
          <KpiValue label="Engagement" value={fmtPct(s.engagement_rate)} />
          <KpiValue label="Avg views"  value={fmtNumber(s.avg_views)} />
          <KpiValue label="Avg likes"  value={fmtNumber(s.avg_likes)} />
          <KpiValue label="Posts"      value={fmtNumber(s.posts_count)} />
          <KpiValue label="Verticals"  value={(s.secondary_categories.length + (s.primary_category ? 1 : 0)).toString()} />
          <KpiValue label="Niche"      value={s.primary_category ?? 'Multi'} />
          <KpiValue label="Profile"    value={s.platform} />
        </div>
      </div>
    </SectionCard>
  );
}

function AudiencePanel({ sections }: { sections: FreeReportSections }) {
  const a = sections.audience;
  const malePct = a.gender_split.male ?? 0;
  const femalePct = a.gender_split.female ?? 0;
  const credibility = a.credibility_score ?? null;

  // Stacked age × gender (when split data is available)
  const stackedAge = useMemo(() => {
    const overall = a.age_distribution ?? [];
    const male = a.age_distribution_male ?? [];
    const female = a.age_distribution_female ?? [];
    if (overall.length === 0 && male.length === 0) return null;
    const labels = (overall.length ? overall : male).map((r) => r.label);
    return labels.map((label) => {
      const m = male.find((r) => r.label === label)?.followers_pct ?? null;
      const f = female.find((r) => r.label === label)?.followers_pct ?? null;
      const o = overall.find((r) => r.label === label)?.followers_pct ?? null;
      return {
        name: label,
        Male:   Math.round((m ?? (o ? o * malePct   : 0)) * 100),
        Female: Math.round((f ?? (o ? o * femalePct : 0)) * 100),
      };
    });
  }, [a.age_distribution, a.age_distribution_male, a.age_distribution_female, malePct, femalePct]);

  const topCountries = (a.top_countries ?? [])
    .slice(0, 8)
    .map((r) => ({ name: r.label, value: Math.round((r.followers_pct ?? 0) * 100) }));

  const topInterests = (a.audience_interests ?? [])
    .slice(0, 8)
    .map((r) => ({ name: r.label, value: Math.round((r.followers_pct ?? 0) * 100) }));

  const radarData = [
    { axis: 'Authenticity',  value: Math.round((credibility ?? 0.5) * 100) },
    { axis: 'Engagement',    value: Math.round(((sections.snapshot.engagement_rate ?? 0.02) / 0.10) * 100) },
    { axis: 'Reach quality', value: a.reachability_label === 'high' ? 90 : a.reachability_label === 'medium' ? 60 : 35 },
    { axis: 'Brand fit',     value: 70 },
    { axis: 'Geographic',    value: topCountries.length > 0 ? 75 : 40 },
  ];

  return (
    <SectionCard
      eyebrow="02 — Audience Intelligence"
      title="Who follows you"
      subtitle="Audience composition, interests, and quality signals."
      variant="elevated"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 l-card p-5">
          <div className="l-section-eyebrow mb-3">Gender split</div>
          <Donut
            data={[
              { name: 'Female', value: Math.round(femalePct * 100) },
              { name: 'Male',   value: Math.round(malePct * 100) },
            ]}
            centerLabel="Audience"
            centerValue={femalePct >= malePct ? `${Math.round(femalePct * 100)}% F` : `${Math.round(malePct * 100)}% M`}
            height={220}
          />
          <ChartLegend
            items={[
              { name: 'Female', value: fmtPct(femalePct), color: CHART_PALETTE[0] },
              { name: 'Male',   value: fmtPct(malePct),   color: CHART_PALETTE[1] },
            ]}
          />
        </div>

        <div className="lg:col-span-7 l-card p-5">
          <div className="flex items-baseline justify-between mb-3">
            <div className="l-section-eyebrow">Age distribution</div>
            <div className="l-subtle text-xs">Stacked by gender</div>
          </div>
          {stackedAge ? (
            <StackedBar
              data={stackedAge}
              series={[
                { key: 'Female', name: 'Female', color: CHART_PALETTE[0] },
                { key: 'Male',   name: 'Male',   color: CHART_PALETTE[1] },
              ]}
              height={240}
            />
          ) : (
            <p className="l-subtle text-sm">Age distribution unavailable for this profile.</p>
          )}
        </div>

        <div className="lg:col-span-6 l-card p-5">
          <div className="l-section-eyebrow mb-3">Top audience countries</div>
          {topCountries.length > 0 ? (
            <HorizontalBar data={topCountries} valueFormatter={(v) => `${v}%`} height={220} />
          ) : (
            <p className="l-subtle text-sm">No geographic data available.</p>
          )}
        </div>

        <div className="lg:col-span-6 l-card p-5">
          <div className="l-section-eyebrow mb-3">Audience interests</div>
          {topInterests.length > 0 ? (
            <HorizontalBar data={topInterests} valueFormatter={(v) => `${v}%`} height={220} color={CHART_PALETTE[2]} />
          ) : (
            <p className="l-subtle text-sm">No audience interest data available.</p>
          )}
        </div>

        <div className="lg:col-span-5 l-card p-5">
          <div className="l-section-eyebrow mb-3">Audience quality</div>
          <RadarScore data={radarData} height={240} />
        </div>

        <div className="lg:col-span-7 l-card p-5">
          <div className="l-section-eyebrow mb-3">Brand affinity</div>
          {(a.brand_affinity ?? []).slice(0, 8).length > 0 ? (
            <ul className="space-y-2">
              {(a.brand_affinity ?? []).slice(0, 8).map((b) => (
                <li key={b.label} className="flex items-center justify-between text-sm">
                  <span style={{ color: 'var(--ch2-ink)' }}>{b.label}</span>
                  <span className="font-medium" style={{ color: 'var(--ch2-ink-2)' }}>
                    {fmtPct(b.followers_pct)}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="l-subtle text-sm">No brand affinity data available.</p>
          )}
        </div>

        {(a.audience_languages || a.top_cities) && (
          <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {a.top_cities && a.top_cities.length > 0 && (
              <div className="l-card p-5">
                <div className="l-section-eyebrow mb-3">Top cities</div>
                <ul className="space-y-1.5 text-sm">
                  {a.top_cities.slice(0, 6).map((c) => (
                    <li key={c.name} className="flex justify-between">
                      <span style={{ color: 'var(--ch2-ink)' }}>{c.name}</span>
                      <span className="l-subtle">{(c.weight * 100).toFixed(1)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {a.audience_languages && a.audience_languages.length > 0 && (
              <div className="l-card p-5">
                <div className="l-section-eyebrow mb-3">Languages</div>
                <ul className="space-y-1.5 text-sm">
                  {a.audience_languages.slice(0, 6).map((l) => (
                    <li key={l.name} className="flex justify-between">
                      <span style={{ color: 'var(--ch2-ink)' }}>{l.name}</span>
                      <span className="l-subtle">{(l.weight * 100).toFixed(1)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function CohortPanel({ sections }: { sections: FreeReportSections }) {
  const c = sections.cohort;
  const peers = useMemo(() => {
    // Synthesize peer cloud around the cohort medians for the scatter.
    const cloud: Array<{ x: number; y: number; size?: number; isYou?: boolean }> = [];
    const baseEng = (c.engagement_p50 ?? 0.03);
    const baseInc = c.income_avg ?? 5000;
    for (let i = 0; i < 18; i++) {
      cloud.push({
        x: baseEng * (0.4 + Math.random() * 1.6),
        y: baseInc * (0.3 + Math.random() * 2.2),
        size: 80,
      });
    }
    if (c.your_engagement != null) {
      cloud.push({
        x: c.your_engagement,
        y: c.income_avg ?? baseInc,
        isYou: true,
        size: 280,
      });
    }
    return cloud;
  }, [c.engagement_p50, c.income_avg, c.your_engagement]);

  return (
    <SectionCard
      eyebrow="03 — Cohort Benchmark"
      title="How you compare to peers"
      subtitle={c.cohort_label}
      variant="elevated"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 grid grid-cols-3 lg:grid-cols-1 gap-3">
          <BenchmarkBand label="Low"  value={fmtUsd(c.income_low)}  emphasize={false} />
          <BenchmarkBand label="Avg"  value={fmtUsd(c.income_avg)}  emphasize />
          <BenchmarkBand label="High" value={fmtUsd(c.income_high)} emphasize={false} />
        </div>

        <div className="lg:col-span-8 l-card p-5">
          <div className="l-section-eyebrow mb-2">Creators like you (peer cloud)</div>
          <PeerScatter
            data={peers}
            xLabel="Engagement rate"
            yLabel="Estimated monthly revenue ($)"
            xFormatter={(v) => `${(v * 100).toFixed(1)}%`}
            yFormatter={(v) => fmtUsd(v)}
            height={260}
          />
          <p className="l-subtle text-xs mt-2">
            Larger marker is you. Lighter dots are peers in the same cohort.
          </p>
        </div>

        <div className="lg:col-span-12 grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiValue label="Your engagement" value={fmtPct(c.your_engagement)} />
          <KpiValue label="Cohort p50"      value={fmtPct(c.engagement_p50)} />
          <KpiValue label="Cohort p90"      value={fmtPct(c.engagement_p90)} />
          <KpiValue label="Your percentile" value={c.your_percentile != null ? `${c.your_percentile}%` : '—'} />
        </div>

        {c.gap_to_top && (
          <div className="lg:col-span-12 l-card p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="l-section-eyebrow mb-1">Gap to top performers</div>
                <p className="text-sm" style={{ color: 'var(--ch2-ink)' }}>
                  Top 10% of your cohort post {fmtPct(c.gap_to_top.top)} engagement; you sit at {fmtPct(c.gap_to_top.you)}.
                </p>
              </div>
              <Badge variant={c.your_percentile && c.your_percentile >= 75 ? 'success' : 'warning'}>
                {c.your_percentile && c.your_percentile >= 75 ? 'Top performer' : 'Room to grow'}
              </Badge>
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function BenchmarkBand({ label, value, emphasize }: { label: string; value: string; emphasize: boolean }) {
  return (
    <div
      className="p-5"
      style={{
        background: emphasize ? 'var(--ch2-accent-tint)' : 'var(--ch2-surface)',
        border: emphasize ? '1px solid rgba(91,91,214,0.22)' : '1px solid var(--ch2-border)',
        borderRadius: 'var(--ch2-radius-md)',
      }}
    >
      <div className="l-section-eyebrow">{label}</div>
      <div
        className="font-display font-bold mt-1"
        style={{
          fontSize: 26,
          letterSpacing: '-0.02em',
          color: emphasize ? 'var(--ch2-accent-2)' : 'var(--ch2-ink)',
        }}
      >
        {value}
      </div>
      <div className="l-subtle text-xs mt-1">monthly</div>
    </div>
  );
}

function MonetizationPanel({
  sections,
  premium,
}: {
  sections: FreeReportSections;
  premium: PremiumReportSections | null;
}) {
  const m = sections.monetization_overview;
  const donut = m.mix.map((cat) => ({ name: cat.label, value: cat.expected }));

  return (
    <SectionCard
      eyebrow="04 — Monetization Opportunities"
      title="What you could earn"
      subtitle="Estimated annual GMV across the five revenue streams."
      variant="elevated"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 l-card p-5">
          <Donut data={donut} centerLabel="Estimated" centerValue={fmtUsd(m.total_estimated_expected)} height={260} />
          <ChartLegend
            items={m.mix.map((cat, i) => ({
              name: cat.label,
              value: fmtUsd(cat.expected),
              color: CHART_PALETTE[i % CHART_PALETTE.length],
            }))}
          />
        </div>

        <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiValue label="Conservative" value={fmtUsd(m.total_estimated_low)}  hint="low band" />
          <KpiValue label="Expected"     value={fmtUsd(m.total_estimated_expected)} hint="median" />
          <KpiValue label="Stretch"      value={fmtUsd(m.total_estimated_high)} hint="upside" />
          <KpiValue label="Tier"         value={m.tier ? `Tier ${m.tier}` : '—'} />
          <KpiValue label="Confidence"   value={m.confidence ? `${Math.round(m.confidence * 100)}%` : '—'} />
          <KpiValue label="Streams"      value={m.mix.length.toString()} hint="revenue paths" />
        </div>

        <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-5 gap-3">
          {m.mix.map((cat, i) => (
            <div
              key={cat.category}
              className="p-4"
              style={{
                background: cat.locked && !premium ? 'var(--ch2-surface-2)' : 'var(--ch2-surface)',
                border: '1px solid var(--ch2-border)',
                borderRadius: 'var(--ch2-radius-md)',
                position: 'relative',
              }}
            >
              <div
                className="w-2 h-2 rounded-full mb-2"
                style={{ background: CHART_PALETTE[i % CHART_PALETTE.length] }}
              />
              <div className="text-sm font-medium" style={{ color: 'var(--ch2-ink)' }}>{cat.label}</div>
              <div className="font-display font-bold mt-1" style={{ fontSize: 18, color: 'var(--ch2-ink)' }}>
                {fmtUsd(cat.expected)}
              </div>
              <div className="l-subtle text-xs mt-0.5">
                {fmtUsd(cat.low)} – {fmtUsd(cat.high)}
              </div>
              {cat.locked && !premium && (
                <div className="absolute top-3 right-3">
                  <Lock className="w-3.5 h-3.5" style={{ color: 'var(--ch2-accent-2)' }} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="lg:col-span-12">
          <div className="l-card-muted p-5">
            <div className="l-section-eyebrow mb-3">What CreatorHub unlocks</div>
            <ul className="grid md:grid-cols-2 gap-x-6 gap-y-2">
              {m.what_creatorhub_unlocks.map((u) => (
                <li key={u} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--ch2-success)' }} />
                  <span style={{ color: 'var(--ch2-ink)' }}>{u}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {premium && (
          <div className="lg:col-span-12">
            <SectionCard title="Per-category monthly breakdown" variant="flat" className="!shadow-none">
              <div className="space-y-3">
                {premium.monetization_full.by_category_breakdown.map((row) => (
                  <div
                    key={row.category}
                    className="flex flex-col md:flex-row md:items-center justify-between gap-2 p-4"
                    style={{
                      border: '1px solid var(--ch2-border-2)',
                      borderRadius: 'var(--ch2-radius-sm)',
                    }}
                  >
                    <div className="min-w-0">
                      <div className="font-medium" style={{ color: 'var(--ch2-ink)' }}>{row.category}</div>
                      <div className="l-subtle text-xs mt-0.5">{row.drivers.join(' · ')}</div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="neutral">{fmtUsd(row.monthly_low)}</Badge>
                      <Badge variant="accent">{fmtUsd(row.monthly_expected)}</Badge>
                      <Badge variant="success">{fmtUsd(row.monthly_high)}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function ContentPanel({
  sections,
  isPremium,
}: {
  sections: FreeReportSections;
  isPremium: boolean;
}) {
  const c = sections.content_signals;
  const trendData = c.engagement_trend.map((p) => ({ name: p.name, ER: Math.round(p.engagement * 10000) / 100 }));
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = ['6am', '9am', '12pm', '3pm', '6pm', '9pm'];

  return (
    <SectionCard
      eyebrow="05 — Content & Performance"
      title="How your content performs"
      subtitle={c.posting_cadence ?? 'Format mix, engagement trend, and posting timing.'}
      variant="elevated"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 l-card p-5">
          <div className="l-section-eyebrow mb-3">Engagement trend (12 weeks)</div>
          <TrendLine
            data={trendData}
            series={[{ key: 'ER', name: 'Engagement %', color: CHART_PALETTE[0] }]}
            height={220}
          />
        </div>

        <div className="lg:col-span-5 l-card p-5">
          <div className="l-section-eyebrow mb-3">Format mix</div>
          <Donut
            data={c.format_breakdown}
            height={220}
          />
          <ChartLegend
            items={c.format_breakdown.map((f, i) => ({
              name: f.name, value: `${f.value}%`, color: CHART_PALETTE[i % CHART_PALETTE.length],
            }))}
          />
        </div>

        {isPremium && (
          <div className="lg:col-span-12 l-card p-5">
            <div className="l-section-eyebrow mb-3">Posting timing heatmap</div>
            <Heatmap
              data={c.posting_heatmap}
              rows={days}
              cols={hours}
              valueFormatter={(v) => `${v}`}
            />
            <p className="l-subtle text-xs mt-2">
              Darker cells indicate higher engagement-weighted posting density.
            </p>
          </div>
        )}

        {(c.popular_hashtags.length > 0 || c.popular_mentions.length > 0) && (
          <div className="lg:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.popular_hashtags.length > 0 && (
              <div className="l-card p-5">
                <div className="l-section-eyebrow mb-3">Popular hashtags</div>
                <div className="flex flex-wrap gap-2">
                  {c.popular_hashtags.map((h) => (
                    <Badge key={h.tag} variant="neutral">
                      #{h.tag} · {(h.pct * 100).toFixed(1)}%
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {c.popular_mentions.length > 0 && (
              <div className="l-card p-5">
                <div className="l-section-eyebrow mb-3">Popular mentions</div>
                <div className="flex flex-wrap gap-2">
                  {c.popular_mentions.map((m) => (
                    <Badge key={m.mention} variant="info">
                      @{m.mention} · {(m.pct * 100).toFixed(1)}%
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function ProductsPanel({
  premium,
  preview,
}: {
  premium: PremiumReportSections | null;
  preview: FreeReportSections['premium_preview'];
}) {
  if (!premium) {
    return (
      <SectionCard
        eyebrow="06 — Product Matches"
        title="Top product matches"
        subtitle="Premium • ranked products with match score, factor breakdown, and per-product GMV."
        variant="elevated"
      >
        <LockedTeaser
          title="Unlock product matches"
          description={preview.bullets[0]}
          bullets={[
            'Top 5 products ranked by deterministic + AI score',
            'Factor breakdown: tag overlap, audience fit, price, margin',
            'Per-product GMV estimate (low / expected / high)',
            'Direct path to launching a private-label SKU',
          ]}
          cta={{ label: 'Unlock with Pro', href: '/upgrade' }}
        />
      </SectionCard>
    );
  }
  return (
    <SectionCard
      eyebrow="06 — Product Matches"
      title="Top 5 products that fit your audience"
      variant="elevated"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {premium.product_matches.map((p) => (
          <ProductCard key={p.product_id} product={p} />
        ))}
      </div>
    </SectionCard>
  );
}

function ProductCard({ product }: { product: PremiumReportSections['product_matches'][number] }) {
  return (
    <div
      className="overflow-hidden flex flex-col"
      style={{
        background: 'var(--ch2-surface)',
        border: '1px solid var(--ch2-border)',
        borderRadius: 'var(--ch2-radius-md)',
      }}
    >
      <div
        style={{
          aspectRatio: '1 / 1',
          background: 'var(--ch2-surface-2)',
          position: 'relative',
        }}
      >
        {product.primary_image_url ? (
          <img
            src={proxyImageUrl(product.primary_image_url) ?? undefined}
            alt={product.title}
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.opacity = '0';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="w-8 h-8" style={{ color: 'var(--ch2-ink-3)' }} />
          </div>
        )}
        <div className="absolute top-2 left-2">
          <Badge variant="accent">#{product.rank}</Badge>
        </div>
        <div className="absolute top-2 right-2">
          <Badge variant="success">{Math.round(product.score * 100)}</Badge>
        </div>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="text-sm font-medium line-clamp-2" style={{ color: 'var(--ch2-ink)' }}>
          {product.title}
        </div>
        <div className="l-subtle text-xs">{product.vertical} · {product.category}</div>
        {(product.price_min || product.price_max) && (
          <div className="text-sm" style={{ color: 'var(--ch2-ink-2)' }}>
            ${product.price_min} – ${product.price_max}
          </div>
        )}
        <p className="l-muted text-xs mt-1 line-clamp-3">{product.why}</p>
        {product.gmv_expected !== undefined && (
          <div className="mt-auto pt-2" style={{ borderTop: '1px dashed var(--ch2-border)' }}>
            <div className="l-section-eyebrow">Est. GMV</div>
            <div className="text-sm font-semibold" style={{ color: 'var(--ch2-ink)' }}>
              {fmtUsd(product.gmv_expected)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VendorsPanel({
  premium,
  preview,
}: {
  premium: PremiumReportSections | null;
  preview: FreeReportSections['premium_preview'];
}) {
  if (!premium) {
    return (
      <SectionCard
        eyebrow="07 — Vendor Matches"
        title="Vendors that match your audience"
        subtitle="Premium • direct vendor connections with commission ranges."
        variant="elevated"
      >
        <LockedTeaser
          title="Unlock vendor matches"
          description={preview.bullets[1]}
          bullets={[
            'Pre-vetted vendors aligned with your audience verticals',
            'Commission ranges and fulfilment posture',
            'Direct introduction path through CreatorHub',
          ]}
          cta={{ label: 'Unlock with Pro', href: '/upgrade' }}
        />
      </SectionCard>
    );
  }
  return (
    <SectionCard
      eyebrow="07 — Vendor Matches"
      title="Top vendors aligned to your audience"
      variant="elevated"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {premium.vendor_matches.map((v) => (
          <div
            key={v.vendor_id}
            className="p-5 flex flex-col gap-3"
            style={{
              background: 'var(--ch2-surface)',
              border: '1px solid var(--ch2-border)',
              borderRadius: 'var(--ch2-radius-md)',
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="font-medium truncate" style={{ color: 'var(--ch2-ink)' }}>{v.name}</div>
              <Badge variant="success">{Math.round(v.fit_score * 100)}</Badge>
            </div>
            <div className="l-subtle text-xs">Vertical: {v.category}</div>
            <div className="text-sm">
              <span className="l-muted">Commission </span>
              <span className="font-semibold" style={{ color: 'var(--ch2-ink)' }}>
                {v.commission_band.low}%–{v.commission_band.high}%
              </span>
            </div>
            <p className="l-muted text-sm">{v.why}</p>
            <Button size="sm" variant="secondary" iconRight={<ArrowUpRight className="w-3.5 h-3.5" />}>
              Request intro
            </Button>
          </div>
        ))}
      </div>
      {premium.brand_deal_opportunities.length > 0 && (
        <div className="mt-8">
          <div className="l-section-eyebrow mb-3">Brand-deal pipeline</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {premium.brand_deal_opportunities.slice(0, 6).map((b) => (
              <div
                key={b.brand_name}
                className="p-4 flex items-start justify-between gap-3"
                style={{
                  background: 'var(--ch2-surface-2)',
                  border: '1px solid var(--ch2-border-2)',
                  borderRadius: 'var(--ch2-radius-md)',
                }}
              >
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: 'var(--ch2-ink)' }}>{b.brand_name}</div>
                  <div className="l-subtle text-xs mt-0.5">{b.category}</div>
                  <p className="l-muted text-xs mt-1">{b.why}</p>
                </div>
                <div className="text-right shrink-0">
                  <Badge variant="accent">{Math.round(b.fit_score * 100)}</Badge>
                  <div className="text-xs font-medium mt-1" style={{ color: 'var(--ch2-ink)' }}>
                    ${b.estimated_value_low}–${b.estimated_value_high}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function ActionsPanel({
  sections,
  premium,
}: {
  sections: FreeReportSections;
  premium: PremiumReportSections | null;
}) {
  const list = premium?.action_plan_full ?? sections.action_plan_basic;
  return (
    <SectionCard
      eyebrow="08 — Recommended Actions"
      title="Your monetization playbook"
      subtitle="Ordered by impact. Premium actions unlock once you upgrade."
      variant="elevated"
    >
      <ol className="space-y-3">
        {list.map((a) => (
          <li
            key={a.rank}
            className="p-5 flex items-start gap-4"
            style={{
              background: a.premium && !premium ? 'var(--ch2-surface-2)' : 'var(--ch2-surface)',
              border: '1px solid var(--ch2-border)',
              borderRadius: 'var(--ch2-radius-md)',
              opacity: a.premium && !premium ? 0.7 : 1,
            }}
          >
            <div
              className="w-9 h-9 flex items-center justify-center font-semibold shrink-0"
              style={{
                background: 'var(--ch2-accent-tint)',
                color: 'var(--ch2-accent-2)',
                borderRadius: '999px',
              }}
            >
              {a.rank}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium" style={{ color: 'var(--ch2-ink)' }}>{a.title}</div>
                {a.premium && <Badge variant="locked">Pro</Badge>}
                <Badge variant="neutral">Effort: {a.effort}</Badge>
                <Badge variant={a.impact === 'high' ? 'success' : 'info'}>Impact: {a.impact}</Badge>
              </div>
              <p className="l-muted text-sm mt-1">{a.description}</p>
            </div>
          </li>
        ))}
      </ol>
    </SectionCard>
  );
}

function PremiumUnlockBanner({ preview }: { preview: FreeReportSections['premium_preview'] }) {
  return (
    <div
      className="p-8 lg:p-10"
      style={{
        background: 'linear-gradient(135deg, #0E1116 0%, #1a1f2a 100%)',
        color: '#fff',
        borderRadius: 'var(--ch2-radius-lg)',
        boxShadow: 'var(--ch2-shadow-lg)',
      }}
    >
      <div className="grid lg:grid-cols-12 gap-6 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4" style={{ color: '#FFD400' }} />
            <span className="text-sm font-medium" style={{ color: '#FFD400' }}>Premium</span>
          </div>
          <h3 className="font-display font-bold mb-3" style={{ fontSize: 30, letterSpacing: '-0.02em' }}>
            Unlock your full monetization playbook
          </h3>
          <p className="text-base opacity-80 mb-4 leading-relaxed">
            Pro and Scale plans add product matching, vendor connections, brand deal pipeline,
            posting timing intelligence, and the full action plan tailored to your audience.
          </p>
          <ul className="space-y-2 mb-6">
            {preview.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm opacity-90">
                <Heart className="w-4 h-4 mt-0.5 shrink-0" style={{ color: '#FFD400' }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3">
            <a
              href="/upgrade"
              className="inline-flex items-center gap-2 px-5 py-2.5 font-medium text-sm"
              style={{ background: '#FFD400', color: '#0E1116', borderRadius: 'var(--ch2-radius-sm)' }}
            >
              See Pro & Scale plans <ArrowUpRight className="w-4 h-4" />
            </a>
            <span className="text-xs opacity-60">Cancel anytime · 30-day full refund</span>
          </div>
        </div>

        <div className="lg:col-span-5 grid grid-cols-2 gap-3">
          {preview.sections.map((label) => (
            <div
              key={label}
              className="p-4"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--ch2-radius-sm)',
              }}
            >
              <Lock className="w-4 h-4 mb-2 opacity-60" />
              <div className="text-sm font-medium">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
